import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, CardHeader, EmptyState, Skeleton, Pagination } from "@/hq/HQPrimitives";

const STAGES = ["NEW","DISCOVERY","QUALIFIED","QUOTE_PREPARING","QUOTE_SENT","NEGOTIATION","WON","LOST"];
const SERVICES = ["custom_website","landing_page","orange_optimise","orange_care","crm_business_system"];

export default function HQSalesOpportunities() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [stage, setStage] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await hqApi.get("/sales/opportunities", { params: { page, page_size: 25, stage: stage || undefined } });
      setData(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, stage]);

  async function updateStage(id, newStage) {
    try {
      await hqApi.patch(`/sales/opportunities/${id}`, { stage: newStage });
      toast.success(`Stage → ${newStage}`); load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }

  return (
    <Card>
      <CardHeader eyebrow="Sales Engine" title="Opportunities" />
      <div className="px-6 py-4 flex gap-2">
        <select value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }} data-testid="opps-stage-filter" className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px]">
          <option value="">All stages</option>{STAGES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="px-6 pb-6">
        {loading ? <Skeleton h={80} /> : !data.items?.length ? (
          <EmptyState title="No opportunities yet" hint="Create one from a prospect detail." testId="opps-empty" />
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
          <table className="w-full min-w-[560px] text-[13px]">
            <thead><tr className="text-left text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A]">
              <th className="py-2">Service</th><th>Value</th><th>Stage</th><th>Owner</th><th>Updated</th>
            </tr></thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id} className="border-t border-[#EAEAEA]" data-testid={`opportunity-row-${o.id}`}>
                  <td className="py-3 font-semibold">{o.service}</td>
                  <td className="py-3 tabular-nums">{o.currency} {Number(o.estimated_value || 0).toLocaleString()}</td>
                  <td className="py-3">
                    <select value={o.stage} onChange={(e) => updateStage(o.id, e.target.value)} data-testid={`opportunity-stage-${o.id}`} className="px-2 py-1 rounded-full border border-[#EAEAEA] text-[11px] font-bold tracking-[0.12em]">
                      {STAGES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="py-3 text-[12px]">{o.owner_email || "—"}</td>
                  <td className="py-3 text-[11px] text-[#8A8A8A]">{o.updated_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="opps-page" />
      </div>
    </Card>
  );
}
